import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface AcademyTrack {
  id: string;
  name: string;
  description: string;
  difficulty: string;
  category: string;
  modules_count: number;
  estimated_hours: number;
  enrollment_count: number;
  status: string;
}

interface Enrollment {
  id: string;
  track_id: string;
  track_name: string;
  progress_percent: number;
  status: string;
  enrolled_at: string;
}

const difficultyColors: Record<string, string> = {
  beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  advanced: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export function Academy() {
  const queryClient = useQueryClient();

  const { data: tracksData, isLoading: tracksLoading } = useQuery({
    queryKey: ['academy-tracks'],
    queryFn: () => api<{ tracks: AcademyTrack[] }>('academy/tracks'),
  });

  const { data: progressData } = useQuery({
    queryKey: ['academy-progress'],
    queryFn: () => api<{ enrollments: Enrollment[] }>('academy/progress'),
  });

  const enrollMutation = useMutation({
    mutationFn: (trackId: string) =>
      api('academy/enroll', { body: { track_id: trackId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['academy-progress'] });
      queryClient.invalidateQueries({ queryKey: ['academy-tracks'] });
    },
  });

  const tracks = tracksData?.tracks ?? [];
  const enrollments = progressData?.enrollments ?? [];
  const enrolledTrackIds = new Set(enrollments.map((e) => e.track_id));

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Academy</h1>
        <p className="text-muted-foreground">Learn AI, earn certifications, and contribute knowledge.</p>
      </div>

      {/* Active enrollments */}
      {enrollments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {enrollments.map((enrollment) => (
              <div key={enrollment.id} className="rounded-lg border bg-card p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium">{enrollment.track_name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    enrollment.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {enrollment.status}
                  </span>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{enrollment.progress_percent}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${enrollment.progress_percent}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available tracks */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Learning Tracks</h2>
        {tracksLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : tracks.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
            No learning tracks available yet. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tracks.map((track) => {
              const isEnrolled = enrolledTrackIds.has(track.id);
              return (
                <div key={track.id} className="rounded-lg border bg-card p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium">{track.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColors[track.difficulty] ?? difficultyColors['beginner']}`}>
                      {track.difficulty}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {track.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <span>{track.modules_count} modules</span>
                    <span>{track.estimated_hours}h estimated</span>
                    <span>{track.enrollment_count} enrolled</span>
                  </div>
                  <button
                    onClick={() => enrollMutation.mutate(track.id)}
                    disabled={isEnrolled || enrollMutation.isPending}
                    className={`w-full py-2 rounded-md text-sm font-medium ${
                      isEnrolled
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                  >
                    {isEnrolled ? 'Enrolled' : 'Enroll'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
